import { Injectable } from '@angular/core';
import { Http, QueryBus, Weaver, WeaverBase } from '@skylab-angular-shared/core';
import { deserializeInventoryDocumentBuyerModel, deserializeInventoryDocumentClientModel, serializeDocumentExchangeRateModel, serializeInventoryDocumentBuyerModel, serializeInventoryDocumentClientModel } from '@skylab-shared/business';
import { HttpService, ObservableCollection, QueryBus as QueryBusBase, Repository, RepositoryBase, TaggedUrl, Tags } from '@skylab-shared/core';
import { CalculationMethod, ExchangeRateModel, InventoryDocumentItemModel, InventoryGetGoodsReceiptDocumentDTO, InventoryGoodsReceiptAdjustmentDocumentModel, InventoryUpdateGoodsReceiptDocumentDTO, RelatedEntity, VatTableItemModel } from '@skylab-shared/dataModel';
import { getStartOfDate, pickBy } from '@skylab-shared/utils';
import { ProductRepository } from '../product/product.repo';
import { PurchaseInvoiceAdjustmentDocumentRepository } from '../purchase/purchase-invoice-adjustment-document.repo';
import { InventoryGoodsReceiptAdjustmentDocumentBO } from './inventory-goods-receipt-adjustment-document.bo';

@Repository({
    boType: InventoryGoodsReceiptAdjustmentDocumentBO,
    apiController: TaggedUrl.join('inventory/goodsReceipt/adjustments', [Tags.Api]),
    dto: {
        get: InventoryGetGoodsReceiptDocumentDTO,
        update: InventoryUpdateGoodsReceiptDocumentDTO
    }
})
@Injectable({ providedIn: 'root' })
export class InventoryGoodsReceiptAdjustmentDocumentRepository extends RepositoryBase<InventoryGoodsReceiptAdjustmentDocumentBO, InventoryGoodsReceiptAdjustmentDocumentModel, InventoryGetGoodsReceiptDocumentDTO, InventoryUpdateGoodsReceiptDocumentDTO> {

    public constructor(
        private readonly weaver: Weaver,
        private readonly queryBus: QueryBus,
        private readonly productRepository: ProductRepository,
        private readonly purchaseInvoiceAdjustmentRepository: PurchaseInvoiceAdjustmentDocumentRepository,
        http: Http
    ) {
        super(http as HttpService);
    }

    protected createBO(): InventoryGoodsReceiptAdjustmentDocumentBO {
        return new InventoryGoodsReceiptAdjustmentDocumentBO(this.weaver as WeaverBase, this.productRepository, this.queryBus as any as QueryBusBase, this.purchaseInvoiceAdjustmentRepository);
    }

    //#region Mapping
    protected async preparePlainObjectAsync(model: InventoryGoodsReceiptAdjustmentDocumentModel): Promise<InventoryUpdateGoodsReceiptDocumentDTO> {
        return {
            duplicatePreventionId: model.duplicatePreventionId,
            id: model.id,
            version: model.version,
            currencyId: model.currencyId,
            calculationMethod: model.calculationMethod,
            relatedCommercialDocuments: model.relatedCommercialDocuments?.map(doc => ({
                id: doc.id,
                version: doc.version
            })) || [],
            creationMethod: model.creationMethod,
            costCalculationMethod: model.costCalculationMethod,
            issuer: model.issuer,
            adjustedDocumentVersion: model.adjustedDocumentVersion,
            adjustedDocumentId: model.adjustedDocumentId,
            note: model.note,
            recipient: model.recipient,
            issueDate: model.issueDate ? getStartOfDate(model.issueDate) : null,
            numberingId: model.numberingId,
            receiptDate: getStartOfDate(model.receiptDate),
            grossValue: model.grossValue,
            netValue: model.netValue,
            transactionLocation: model.transactionLocation,
            documentNumber: model.documentNumber,
            exchangeRate: model.exchangeRate ? serializeDocumentExchangeRateModel(model.exchangeRate) : null,
            items: model.items.map(elm => ({
                id: elm.id,
                discountValue: elm.discountValue,
                discountKind: elm.discountKind,
                no: elm.ordinalNumber,
                note: elm.note,
                relatedCommercialItemsIds: elm.relatedCommercialItemsIds || [],
                productId: elm.productId,
                productName: elm.productName,
                adjustedItemId: elm.adjustedItemId,
                pkwiu: elm.pkwiu,
                cnCode: elm.cnCode,
                symbol: elm.symbol,
                barCode: elm.barCode,
                jpkVatProductGroup: elm.jpkVatGroup,
                quantity: elm.quantity,
                uniqueKey: elm.uniqueKey,
                unitId: elm.unitId,
                price: model.calculationMethod === CalculationMethod.Gross ? elm.price_grossBeforeDiscount : elm.price_netBeforeDiscount,
                vatRateId: elm.vatRateId
            })),
            inventoryId: model.inventoryId,
            kind: model.kind,
            seller: serializeInventoryDocumentClientModel(model.seller),
            supplier: serializeInventoryDocumentBuyerModel(model.supplier)
        };
    }

    protected async fromPlainObjectAsync(item: InventoryGetGoodsReceiptDocumentDTO): Promise<InventoryGoodsReceiptAdjustmentDocumentModel> {
        return new InventoryGoodsReceiptAdjustmentDocumentModel(pickBy<InventoryGoodsReceiptAdjustmentDocumentModel>({
            duplicatePreventionId: item.duplicatePreventionId,
            id: item.id,
            version: item.version,
            currencyId: item.currencyId,
            calculationMethod: item.calculationMethod,
            relatedCommercialDocuments: item.relatedCommercialDocuments?.map(doc => new RelatedEntity(doc.id, doc.version)) || [],
            costCalculationMethod: item.costCalculationMethod,
            grossValue: item.grossValue,
            netValue: item.netValue,
            issuer: item.issuer,
            adjustedDocumentVersion: item.adjustedDocumentVersion,
            adjustedDocumentId: item.adjustedDocumentId,
            recipient: item.recipient,
            issueDate: item.issueDate,
            note: item.note,
            numberingId: item.numberingId,
            kind: item.kind,
            receiptDate: item.receiptDate,
            transactionLocation: item.transactionLocation,
            issuedByVatPayer: item.issuedByVatPayer,
            documentNumber: item.documentNumber,
            inventoryId: item.inventoryId,
            items: new ObservableCollection(item.items.map(documentItem => {
                return new InventoryDocumentItemModel(pickBy<Partial<InventoryDocumentItemModel>>({
                    ordinalNumber: documentItem.no,
                    discountKind: documentItem.discountKind,
                    discountValue: documentItem.discountValue,
                    note: documentItem.note,
                    price_grossAfterDiscount: documentItem.unitGrossPriceAfterDiscount,
                    price_grossBeforeDiscount: documentItem.unitGrossPrice,
                    price_netAfterDiscount: documentItem.unitNetPriceAfterDiscount,
                    price_netBeforeDiscount: documentItem.unitNetPrice,
                    id: documentItem.id,
                    productId: documentItem.productId,
                    productName: documentItem.productName,
                    pkwiu: documentItem.pkwiu,
                    cnCode: documentItem.cnCode,
                    symbol: documentItem.symbol,
                    barCode: documentItem.barCode,
                    relatedCommercialItemsIds: documentItem.relatedCommercialItemsIds || [],
                    jpkVatGroups: documentItem.jpkVatProductGroup ? [documentItem.jpkVatProductGroup] : [],
                    quantity: documentItem.quantity,
                    uniqueKey: documentItem.uniqueKey,
                    adjustedItemId: documentItem.adjustedItemId,
                    unitId: documentItem.unitId,
                    value_grossAfterDiscount: documentItem.grossValueAfterDiscount,
                    value_grossBeforeDiscount: documentItem.grossValue,
                    value_netAfterDiscount: documentItem.netValueAfterDiscount,
                    value_netBeforeDiscount: documentItem.netValue,
                    value_vatAfterDiscount: documentItem.vatValueAfterDiscount,
                    value_vatBeforeDiscount: documentItem.vatValue,
                    vatRateId: documentItem.vatRateId,
                    inventoryTotalCost: documentItem.inventoryTotalCost,
                    inventoryUnitCost: documentItem.inventoryUnitCost,
                }, val => val !== void 0));
            })),
            seller: deserializeInventoryDocumentClientModel(item.seller),
            supplier: deserializeInventoryDocumentBuyerModel(item.supplier),
            exchangeRate: item.exchangeRate ? new ExchangeRateModel(pickBy<ExchangeRateModel>({
                date: item.exchangeRate.date,
                manual: item.exchangeRate.manual,
                rate: item.exchangeRate.rate,
                tableNo: item.exchangeRate.tableNo,
                units: item.exchangeRate.units
            }, val => val !== void 0)) : null,
            vatTableItems: new ObservableCollection(item.vatTable.items.map(item => {
                return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
                    grossValue: item.currencyGross ?? item.gross,
                    netValue: item.currencyNet ?? item.net,
                    vatRateId: item.vatRateId,
                    vatValue: item.currencyVat ?? item.vat,
                    cost: item.cost
                }, val => val !== void 0));
            })),
            vatTableItemsInBaseCurrency: item.exchangeRate ? new ObservableCollection(item.vatTable.items.map(item => {
                return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
                    grossValue: item.gross,
                    netValue: item.net,
                    vatRateId: item.vatRateId,
                    vatValue: item.vat,
                    cost: item.cost
                }, val => val !== void 0));
            })) : new ObservableCollection()
        }, val => val !== void 0));
    }
    //#endregion
}
